import { Button } from '@mui/material'
import dayjs from 'dayjs'
import capitalize from 'lodash/capitalize'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getMeetingSrcIcon, getMeetingSrcLabel } from 'src/consumer/helpers/meetingSrcUtils'
import { getExactTimeDiff } from 'src/consumer/helpers/utilFunctions'
import { preparePatientLabelFromPatientInfoObject } from 'src/supportPortal/views/scheduledPatients/utils'
import { shouldShowPrescriptionPdfDownloadButtons } from 'src/clinic/components/ClinicManageBookingsPage/utils'
import ContactDetailsTableItem from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/ContactDetailsTableItem'
import InTherapyButton from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/InTherapyButton'
import ConsultsActions from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsActions'
import ConsultsDetailsCell from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsDetailsCell'
import ConsultsVitalsCell from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsVitalsCell'

/**
 * Mobile-first card for a single appointment. Stacks all the information the
 * desktop table spreads across columns (appointment, patient, type & status,
 * timing, vitals, view, actions) into a vertical, touch-friendly card so the
 * Appointments tab never needs horizontal scrolling on small screens.
 */
export default function ConsultsCard({
  bookedSlot,
  viewButtonClick,
  showActionButtons = false,
  hidePatientContactDetails = false,
  mentorProfileData,
  clinicLocationData,
  handleNewTherapyRequest,
  setClickedBookedSlotData,
  therapyBookingDataForThisEvent,
  setEditPatientDialogOpen,
  forMentor,
  setAddVitalsDialogOpen,
  setEditBookingDialogOpen,
  refreshBookedSlots,
  prescriptionViewSettings,
  sendPrescriptionPdfApiSignature,
  prescriptionGetApiSignature,
  prescriptionSaveApiSignature,
  fileUploadApiSignature,
  showGalleryForPrescriptionImages,
  sendSecureSessionLinkToPatientSignature,
  sendPublicSessionLinkToPatientSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
}) {
  const patientInfo = bookedSlot?.patientInfo
  const visitNo = bookedSlot?.patientMeetingSeqNumber
  const meetingSrc = bookedSlot?.meetingSrc
  const isCompleted = Boolean(bookedSlot?.markedCompleted)

  const slotStart = bookedSlot?.startTime ? dayjs.utc(bookedSlot.startTime).local() : null
  const sessionStart = bookedSlot?.sessionStartedAt
    ? dayjs.utc(bookedSlot.sessionStartedAt).local()
    : null
  let waitContent = '--'
  if (sessionStart && slotStart) {
    waitContent = getExactTimeDiff(slotStart, sessionStart, false, false, false)
  } else if (slotStart && slotStart.isSame(dayjs(), 'day')) {
    waitContent = getExactTimeDiff(slotStart, dayjs(), false, false, false)
  }

  const onOpenVitals = setAddVitalsDialogOpen
    ? (slot) => {
        setClickedBookedSlotData?.(slot)
        setAddVitalsDialogOpen(true)
      }
    : undefined

  return (
    <div
      style={{
        border: '1px solid',
        borderColor: isCompleted ? 'rgba(16, 185, 129, 0.35)' : 'rgba(99, 102, 241, 0.18)',
        borderLeft: `4px solid ${isCompleted ? '#10b981' : '#6366F1'}`,
        borderRadius: '14px',
        background: isCompleted ? 'rgba(209, 250, 229, 0.45)' : '#ffffff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* Header: token · mode · time */}
      <div className="d-flex align-items-center justify-content-between" style={{ gap: '8px' }}>
        <div className="d-flex align-items-center" style={{ gap: '10px', minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: '17px', color: '#4338CA', flexShrink: 0 }}>
            #{bookedSlot?.appointmentNumber}
          </span>
          <span
            className="d-flex align-items-center"
            style={{ gap: '4px', fontSize: '12px', color: '#475569', minWidth: 0 }}
          >
            {getMeetingSrcIcon(meetingSrc, { size: 16, color: '#6366F1' })}
            <span style={{ whiteSpace: 'nowrap' }}>{getMeetingSrcLabel(meetingSrc)}</span>
          </span>
        </div>
        <span style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a', flexShrink: 0 }}>
          {slotStart ? slotStart.format(MOMENT_FORMATS.formattedTime24Hrs) : '--'}
        </span>
      </div>

      {/* Patient */}
      <div className="d-flex flex-column" style={{ gap: '2px' }}>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
          {bookedSlot?.therapyPatient && (
            <InTherapyButton bookedSlot={bookedSlot} slotBlocked={false} />
          )}
          <span style={{ fontWeight: 600, fontSize: '15px' }}>
            {preparePatientLabelFromPatientInfoObject(patientInfo, true)}
          </span>
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          {getAgeFromDobWithTime(patientInfo?.dateOfBirth)} Y / {capitalize(patientInfo?.gender)}
          {visitNo ? ` · Visit #${visitNo}` : ''}
          {patientInfo?.patientNumber ? ` · ID ${patientInfo.patientNumber}` : ''}
        </div>
        <ContactDetailsTableItem
          bookedEventInfo={bookedSlot}
          isTableItem={false}
          hidePatientContactDetails={hidePatientContactDetails}
        />
      </div>

      {/* Type, status, screening */}
      <ConsultsDetailsCell bookedSlot={bookedSlot} />

      {/* Wait + vitals */}
      <div
        className="d-flex align-items-center justify-content-between flex-wrap"
        style={{ gap: '8px' }}
      >
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          Wait: <strong style={{ color: '#334155' }}>{waitContent}</strong>
        </span>
        <ConsultsVitalsCell bookedSlot={bookedSlot} onOpenVitals={onOpenVitals} />
      </div>

      {/* Actions */}
      <div
        className="d-flex align-items-center"
        style={{ gap: '8px', borderTop: '1px solid rgba(15, 23, 42, 0.06)', paddingTop: '10px' }}
      >
        {viewButtonClick ? (
          <Button
            variant="contained"
            onClick={() => viewButtonClick(bookedSlot)}
            sx={{ flex: 1, textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}
          >
            View
          </Button>
        ) : (
          <span style={{ flex: 1, fontSize: '12px', color: '#475569', fontWeight: 600 }}>
            {bookedSlot?.markedCompleted
              ? 'Completed'
              : bookedSlot?.sessionStartedAt
              ? 'In Progress'
              : 'Upcoming'}
          </span>
        )}
        {showActionButtons && (
          <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
            <ConsultsActions
              bookedSlot={bookedSlot}
              mentorProfileData={mentorProfileData}
              clinicLocationData={clinicLocationData}
              handleNewTherapyRequest={handleNewTherapyRequest}
              setClickedBookedSlotData={setClickedBookedSlotData}
              therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
              setEditPatientDialogOpen={setEditPatientDialogOpen}
              forMentor={forMentor}
              setAddVitalsDialogOpen={setAddVitalsDialogOpen}
              setEditBookingDialogOpen={setEditBookingDialogOpen}
              refreshBookedSlots={refreshBookedSlots}
              showPrescriptionPdfDownloadButtons={shouldShowPrescriptionPdfDownloadButtons({
                forMentor,
                bookedSlot,
                prescriptionViewSettingsData: prescriptionViewSettings,
              })}
              sendPrescriptionPdfApiSignature={sendPrescriptionPdfApiSignature}
              prescriptionGetApiSignature={prescriptionGetApiSignature}
              prescriptionSaveApiSignature={prescriptionSaveApiSignature}
              fileUploadApiSignature={fileUploadApiSignature}
              showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
              sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
              sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
              prescriptionImagesGetApiSignature={prescriptionImagesGetApiSignature}
              prescriptionImagesSaveApiSignature={prescriptionImagesSaveApiSignature}
            />
          </div>
        )}
      </div>
    </div>
  )
}
